import { HELD_GIFT_DEFAULT_HOLD_DAYS } from "@givehere/contracts";
import { LegalLink, LegalSection, LegalShell, SupportContact } from "./LegalShell.js";
import { PLATFORM_EIN, PLATFORM_MAILING_ADDRESS, useSite } from "./site.js";

/**
 * Donor and charitable-solicitation disclosures (GH-812, drafted per DEC-022).
 *
 * California Gov. Code §12599.9 (AB 488) asks a charitable fundraising platform to tell a donor,
 * before the gift, who receives it, when it is distributed, what is deducted, and whether it is
 * deductible. This page is that block in full; GH-815 puts the short form on the give screens
 * once the registration number exists. Nothing here may be read as registration having happened —
 * the number is a visible gap until it has.
 *
 * Timing comes from DEC-046 for unclaimed organizations (a check by day N, N below 45) and is a
 * gap for claimed ones: payouts are gated (`FEATURE_ORG_PAYOUTS`) and their schedule is undecided.
 */
export function DisclosuresPage() {
  const { organizationLegalName: entity, productName: product } = useSite();
  return (
    <LegalShell title="Donor disclosures">
      <LegalSection id="operator" title="Who runs this platform">
        <p>
          {product} is a charitable fundraising platform operated by {entity},{" "}
          {PLATFORM_MAILING_ADDRESS}.
        </p>
      </LegalSection>

      <LegalSection id="recipient" title="Who receives your gift">
        <p>
          Your gift is a charitable contribution to {entity}, made in support of the organization
          you choose. {entity} receives the funds and grants them to that organization. {entity}{" "}
          keeps discretion and control over the funds, and your choice of organization is honoured
          as a preference. The organization has no claim to a particular gift until {entity}{" "}
          approves the grant.
        </p>
      </LegalSection>

      <LegalSection id="timing" title="When the organization receives it">
        <ul>
          <li>
            <strong>Organizations that have claimed their listing:</strong> grants are paid to the
            organization&rsquo;s verified bank account, on its request or on the weekly,
            every-two-weeks, or monthly schedule it sets.
          </li>
          <li>
            <strong>Organizations that have not claimed their listing:</strong> {product} holds the
            gift for the organization until it claims; if it has not claimed within{" "}
            {HELD_GIFT_DEFAULT_HOLD_DAYS} days, the funds are sent by check to its address on record
            with the IRS. See the{" "}
            <LegalLink to="terms" hash="unclaimed">
              Terms
            </LegalLink>
            .
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="deducted" title="What is deducted">
        <p>
          Every gift carries a platform fee, which also pays for payment processing. You may add it
          on top so the whole gift supports the organization &mdash; it is then worked out on the
          total you pay, so nothing is taken from the gift; if you do not, it is deducted from the
          grant. Nothing else is deducted. The exact fee is shown before you confirm.
        </p>
        <p>
          Today the platform fee is 2.9% + $0.30 per gift, the same for every payment method, and
          the smallest online gift is $1.00. If the fee changes, the new fee applies only to gifts
          made after the change.
        </p>
      </LegalSection>

      <LegalSection id="deductibility" title="Tax deductibility">
        <p>
          {entity} is a tax-exempt public charity under Section 501(c)(3) of the Internal Revenue
          Code (EIN {PLATFORM_EIN}). Your receipt names {entity} as the recipient and says whether
          you received goods or services. A ticket is a purchase of admission and is not a donation.
          A grant recommended from a donor-advised fund was already deductible when you funded it.
          We do not give tax advice.
        </p>
      </LegalSection>

      <LegalSection id="standing" title="Organizations on the platform">
        <p>
          Organizations are listed only when they appear in IRS records as tax-exempt, and an
          organization is shown in the app only after its Candid profile is confirmed. A listing
          that has not been claimed does not mean the organization endorses {product}.
        </p>
      </LegalSection>

      <LegalSection id="attorney-general" title="Contacting the Attorney General">
        <p>
          You can find information about charities and fundraising platforms, or file a complaint,
          with the California Attorney General&rsquo;s Registry of Charities and Fundraisers at{" "}
          <a href="https://oag.ca.gov/charities" rel="noreferrer">
            oag.ca.gov/charities
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection id="questions" title="Questions">
        <p>
          Contact <SupportContact />. Refunds are covered in the{" "}
          <LegalLink to="terms" hash="refunds">
            refund policy
          </LegalLink>
          .
        </p>
      </LegalSection>
    </LegalShell>
  );
}
